import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import servicesData from "../data";
import { motion, AnimatePresence } from "framer-motion";

function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState(null);
  const menuRef = useRef(null);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMobileMenu();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCategoryClick = (category) => {
    setOpenCategory(openCategory === category ? null : category);
  };

  return (
    <header className="bg-white py-4 shadow-md sticky top-0 z-50" ref={menuRef}>
      <div className="container mx-auto px-6 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <Link to="/" onClick={closeMobileMenu}>
            <img
              src="/assets/logo.png"
              alt="Keziah Logo"
              className="h-12" // Keziah logo size
            />
          </Link>
          {/* Fino Payments Bank Logo (Visible on All Screen Sizes) */}
          <img
            src="/assets/Fino.png" // Replace with your Fino logo path
            alt="Fino Payments Bank Logo"
            className="h-8" // Adjust height as needed
          />
        </div>
        <div className="flex items-center">
          <nav className="hidden md:block mr-4">
            <ul className="flex space-x-6">
              <li>
                <Link
                  to="/about-us"
                  className="hover:text-blue-500 text-gray-800"
                  onClick={closeMobileMenu}
                >
                  About
                </Link>
              </li>
              <Menu as="li" className="relative">
                <Menu.Button className="hover:text-blue-500 text-gray-800 flex items-center">
                  Services
                  <ChevronDownIcon className="h-5 w-5 ml-1" aria-hidden="true" />
                </Menu.Button>
                <Menu.Items className="absolute left-0 mt-2 w-64 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10 overflow-auto max-h-96">
                  {Object.entries(servicesData).map(
                    ([category, categoryData]) => (
                      <div key={category} className="px-1 py-1 ">
                        <div className="px-4 py-2 text-gray-800 font-bold capitalize">
                          {category.replace(/([A-Z])/g, " $1").trim()}
                        </div>
                        {categoryData.services?.map((service) => (
                          <Menu.Item key={service.name} as="div">
                            {({ active }) => (
                              <Link
                                to={service.link}
                                className={`${
                                  active
                                    ? "bg-blue-600 text-white"
                                    : "text-gray-900"
                                } group flex w-full items-center rounded-md px-4 py-2 text-sm`}
                                onClick={closeMobileMenu}
                              >
                                {service.name}
                              </Link>
                            )}
                          </Menu.Item>
                        ))}
                      </div>
                    )
                  )}
                </Menu.Items>
              </Menu>
              <li>
                <Link
                  to="/faq"
                  className="hover:text-blue-500 text-gray-800"
                  onClick={closeMobileMenu}
                >
                  FAQ
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="hover:text-blue-500 text-gray-800"
                  onClick={closeMobileMenu}
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full transition duration-300 ease-in-out"
                  onClick={closeMobileMenu}
                >
                  Get Started
                </Link>
              </li>
            </ul>
          </nav>
          <button
            id="burger"
            className="md:hidden text-gray-800 focus:outline-none"
            onClick={toggleMobileMenu}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16m-7 6h7"
              ></path>
            </svg>
          </button>
        </div>
      </div>
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.nav
            id="mobile-nav"
            className="md:hidden bg-white py-4 px-6"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3 }}
          >
            <ul className="space-y-4">
              <li className="group relative">
                <ul className="pl-4 space-y-2">
                  {Object.entries(servicesData).map(
                    ([category, categoryData]) => (
                      <li key={category} className="group relative">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleCategoryClick(category);
                          }}
                          className="w-full text-left hover:text-blue-500 text-gray-800 font-bold flex items-center justify-between"
                        >
                          {category.charAt(0).toUpperCase() +
                            category.slice(1)}{" "}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-4 w-4 transition-transform duration-300 ${
                              openCategory === category ? "rotate-180" : ""
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                        <AnimatePresence>
                          {openCategory === category && (
                            <motion.ul
                              className="pl-4 space-y-2"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              {categoryData.services?.map((service) => (
                                <li key={service.name}>
                                  <Link
                                    to={service.link}
                                    className="block hover:text-blue-500 text-gray-800"
                                    onClick={closeMobileMenu}
                                  >
                                    {service.name}
                                  </Link>
                                </li>
                              ))}
                            </motion.ul>
                          )}
                        </AnimatePresence>
                      </li>
                    )
                  )}
                </ul>
              </li>
              <li className="group relative">
                <ul className="pl-4 space-y-2 hidden group-hover:block">
                  <li>
                    <Link
                      to="/about"
                      className="block hover:text-blue-500 text-gray-800"
                      onClick={closeMobileMenu}
                    >
                      About
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/faq"
                      className="block hover:text-blue-500 text-gray-800"
                      onClick={closeMobileMenu}
                    >
                      FAQ
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/contact"
                      className="block hover:text-blue-500 text-gray-800"
                      onClick={closeMobileMenu}
                    >
                      Contact Us
                    </Link>
                  </li>
                </ul>
              </li>
              <li>
                <Link
                  to="/services"
                  className="block bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full transition duration-300 ease-in-out"
                  onClick={closeMobileMenu}
                >
                  Get Started
                </Link>
              </li>
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

export default Header;